/**
 * @file Provides the `Maybe` type and associated utility functions.
 * Includes `Just` and `Nothing` implementations to represent optional values
 * and operations that can safely handle nullable or undefined data.
 */
import { Left, Right } from 'purify-ts';
/**
 * Represents the `Nothing` variant of the `Maybe` type.
 * Used to signify the absence of a value.
 */
class _Nothing {
    /** Create a new nothing. */
    constructor() {
        Object.freeze(this);
    }
    /** @returns `false` as this is always `Nothing`. */
    isJust() {
        return false;
    }
    /** @returns `true` indicating this is `Nothing`. */
    isNothing() {
        return true;
    }
    /**
     * Matches the `Nothing` pattern.
     *
     * @param patterns The pattern to handle `Nothing`.
     * @returns The result of the `Nothing` handler.
     */
    caseOf(patterns) {
        return patterns.Nothing();
    }
    /** @returns This instance as mapping has no effect on `Nothing`. */
    map() {
        return this;
    }
    /** @returns This instance as mapping has no effect on `Nothing`. */
    mapPromise() {
        return Promise.resolve(this);
    }
    /** @returns This instance as mapping has no effect on `Nothing`. */
    chainPromise() {
        return Promise.resolve(this);
    }
    /** @returns This instance as applicative operation has no effect on `Nothing`. */
    ap() {
        return this;
    }
    /**
     * Returns the other `Maybe` as an alternative.
     *
     * @param other The alternative `Maybe`.
     * @returns The other `Maybe`.
     */
    alt(other) {
        return other;
    }
    /**
     * Lazily returns the alternative `Maybe`.
     *
     * @param other A function returning the alternative `Maybe`.
     * @returns The result of calling `other`.
     */
    altLazy(other) {
        return other();
    }
    /** @returns `Nothing` as chaining has no effect on `Nothing`. */
    chain() {
        return this;
    }
    /** @returns `Nothing` as nullable chaining has no effect on `Nothing`. */
    chainNullable() {
        return this;
    }
    /** @returns `Nothing` as flattening does not affect `Nothing`. */
    join() {
        return this;
    }
    /**
     * Reduces with no effect, returning the initial value.
     *
     * @param _ Ignored reducer function.
     * @param initialValue The initial accumulator value.
     * @returns The initial accumulator value.
     */
    reduce(_, initialValue) {
        return initialValue;
    }
    /** @returns This instance as extension has no effect on `Nothing`. */
    extend() {
        return this;
    }
    /**
     * Throw for a `Nothing`, return the value of a `Just`.
     *
     * @throws An error indicating invalid coercion.
     */
    unsafeCoerce() {
        throw new Error('Maybe#unsafeCoerce was ran on a Nothing');
    }
    /**
     * Throw the provided error for a `Nothing`, return the value of a `Just`.
     *
     * @param error The error to throw.
     * @throws The provided error as this is `Nothing`.
     */
    throwOnEmpty(error) {
        throw error;
    }
    /**
     * Returns the default value as this is `Nothing`.
     *
     * @param defaultValue The default value to return.
     * @returns The default value.
     */
    orDefault(defaultValue) {
        return defaultValue;
    }
    /**
     * Lazily returns the default value as this is `Nothing`.
     *
     * @param getDefaultValue A function returning the default value.
     * @returns The result of `getDefaultValue`.
     */
    orDefaultLazy(getDefaultValue) {
        return getDefaultValue();
    }
    /** @returns An empty list as this is `Nothing`. */
    toList() {
        return [];
    }
    /**
     * Maps with no effect, returning the default value.
     *
     * @param _ Ignored mapping function.
     * @param defaultValue The default value to return.
     * @returns The default value.
     */
    mapOrDefault(_, defaultValue) {
        return defaultValue;
    }
    /** @returns `undefined` as this is `Nothing`. */
    extract() {
        return undefined;
    }
    /** @returns `null` as this is `Nothing`. */
    extractNullable() {
        return null;
    }
    /** @returns This instance as `ifJust` has no effect on `Nothing`. */
    ifJust() {
        return this;
    }
    /**
     * Executes the given effect for `Nothing`.
     *
     * @param effect The effect to execute.
     * @returns This instance.
     */
    ifNothing(effect) {
        effect();
        return this;
    }
    /** @returns `Nothing` as filtering has no effect on `Nothing`. */
    filter() {
        return this;
    }
    /**
     * Converts this `Nothing` to a `Left` in an `Either`.
     *
     * @param value The value to include in the `Left`.
     * @returns A `Left` containing the provided value.
     */
    toEither(value) {
        return Left(value);
    }
    /**
     * Check if the other value is equal to this one.
     *
     * @param other The parameter to check against.
     * @returns `true` if the given value is a `Nothing`.
     */
    equals(other) {
        return other === this;
    }
    /** @returns A string representation of `Nothing`. */
    toString() {
        return 'Nothing';
    }
    /** @returns This value as JSON object. */
    toJSON() {
        return undefined;
    }
}
/**
 * A singleton instance of the `Nothing` class.
 */
// eslint-disable-next-line  @typescript-eslint/no-redeclare
export const Nothing = new _Nothing();
/**
 * Represents the `Just` variant of the `Maybe` type.
 *
 * Encapsulates a value that is present and provides various operations to
 * manipulate or retrieve the value.
 */
export class Just {
    value;
    /**
     * Creates a `Just` instance.
     *
     * @param value The value to encapsulate.
     */
    constructor(value) {
        this.value = value;
    }
    /**
     * Checks if this is a `Just`.
     *
     * @returns `true` as this is always a `Just`.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.isJust()); // true
     *
     * const nothing = Nothing;
     * console.log(nothing.isJust()); // false
     */
    isJust() {
        return true;
    }
    /**
     * Checks if this is a `Nothing`.
     *
     * @returns `false` as this is always a `Just`.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.isNothing()); // false
     *
     * const nothing = Nothing;
     * console.log(nothing.isNothing()); // true
     */
    isNothing() {
        return false;
    }
    /**
     * Checks for equality between this `Just` and another `Maybe`.
     *
     * The values are compared using their identity, meaning most of the time you only want to do
     * this when the maybe stores a primitive.
     *
     * @param other The other `Maybe` to compare.
     * @returns `true` if both are `Just` and their values are equal, otherwise `false`.
     * @example
     * const maybe1 = new Just(5);
     * const maybe2 = new Just(5);
     * const maybe3 = new Just(10);
     * const maybe4 = new Just({});
     * const maybe5 = new Just({});
     * const nothing = Nothing;
     *
     * console.log(maybe1.equals(maybe2)); // true
     * console.log(maybe1.equals(maybe3)); // false
     * console.log(maybe4.equals(maybe5)); // false
     * console.log(maybe1.equals(nothing)); // false
     */
    equals(other) {
        if (other.isJust() && this.isJust()) {
            return other.value === this.value;
        }
        return this.isNothing();
    }
    /**
     * Matches on the `Just` variant using the provided pattern.
     *
     * @param patterns The pattern to handle the `Just` variant.
     * @returns The result of applying the `Just` handler.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.caseOf({ Just: x => x * 2 })); // 10
     *
     * const nothing = Nothing;
     * console.log(nothing.caseOf({ Nothing: () => 'No value' })); // 'No value'
     */
    caseOf(patterns) {
        return patterns.Just(this.value);
    }
    /**
     * Transforms the value in the `Just` using the provided function.
     *
     * @param f The function to apply to the encapsulated value.
     * @returns A new `Just` with the transformed value.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.map(x => x * 2)); // Just(10)
     *
     * const nothing = Nothing;
     * console.log(nothing.map(x => x * 2)); // Nothing
     */
    map(f) {
        return new Just(f(this.value));
    }
    /**
     * Transforms the value in the `Just` using the provided function that returns a promise.
     *
     * @param f The function to apply to the encapsulated value.
     * @returns A promise that resolves to a new `Just` with the transformed value.
     * @example
     * const maybe = new Just(5);
     * console.log(await maybe.map(async x => x * 2)); // Just(10)
     *
     * const nothing = Nothing;
     * console.log(await nothing.map(async x => x * 2)); // Nothing
     */
    async mapPromise(f) {
        return new Just(await f(this.value));
    }
    /**
     * Applies a `Maybe` function to the value in this `Just`.
     *
     * @param maybeF A `Maybe` containing a function to apply.
     * @returns A new `Just` with the result of the function application, or `Nothing` if `maybeF` is `Nothing`.
     * @example
     * const maybe = new Just(5);
     * const maybeF = new Just(x => x * 2);
     * console.log(maybe.ap(maybeF)); // Just(10)
     *
     * const nothing = Nothing;
     * console.log(maybe.ap(nothing)); // Nothing
     * console.log(nothing.ap(maybeF)); // Nothing
     */
    ap(maybeF) {
        if (maybeF.isJust()) {
            return new Just(maybeF.value(this.value));
        }
        return Nothing;
    }
    /**
     * Returns this `Just`, ignoring the provided alternative.
     *
     * @returns This `Just`.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.alt(new Just(10))); // Just(5)
     *
     * const nothing = Nothing;
     * console.log(nothing.alt(new Just(10))); // Just(10)
     */
    alt() {
        return this;
    }
    /**
     * Returns this `Just`, ignoring the lazily provided alternative.
     *
     * @returns This `Just`.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.altLazy(() => new Just(10))); // Just(5)
     *
     * const nothing = Nothing;
     * console.log(nothing.altLazy(() => new Just(10))); // Just(10)
     */
    altLazy() {
        return this;
    }
    /**
     * Chains a nullable-producing function to this `Just`.
     *
     * @param f A function that may return a nullable value.
     * @returns A `Just` with the non-null result of `f`, or `Nothing` if `f` returns null/undefined.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.chainNullable(x => x > 3 ? x * 2 : null)); // Just(10)
     * console.log(maybe.chainNullable(x => x < 3 ? x * 2 : null)); // Nothing
     *
     * const nothing = Nothing;
     * console.log(nothing.chainNullable(10 => 10)); // Nothing
     */
    chainNullable(f) {
        const out = f(this.value);
        return out == null ? Nothing : new Just(out);
    }
    /**
     * Chains another `Maybe` operation using the encapsulated value.
     *
     * @param f A function that returns a `Maybe` when applied to the encapsulated value.
     * @returns The result of the function.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.chain(x => x > 3 ? new Just(x * 2) : Nothing)); // Just(10)
     *
     * console.log(maybe.chain(x => x > 10 ? new Just(x * 2) : Nothing)); // Nothing
     *
     * const nothing = Nothing;
     * console.log(nothing.chain(x => new Just(x * 2))); // Nothing
     */
    chain(f) {
        return f(this.value);
    }
    /**
     * Chains a function that returns a promise of a maybe.
     *
     * @param f A function that returns a promise to a `Maybe` when applied to the encapsulated
     * value.
     * @returns The awaited result of the function.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.chain(async x => x > 3 ? new Just(x * 2) : Nothing)); // Promise<Just(10)>
     *
     * console.log(maybe.chain(async x => x > 10 ? new Just(x * 2) : Nothing)); // Promise<Nothing>
     *
     * const nothing = Nothing;
     * console.log(nothing.chain(async x => new Just(x * 2))); // Promise<Nothing>
     */
    async chainPromise(f) {
        return await f(this.value);
    }
    /**
     * Flattens a nested `Maybe` into a single `Maybe`.
     *
     * @returns The inner `Maybe` if this is a `Just` of a `Maybe`, otherwise this.
     * @example
     * const maybe = new Just(new Just(5));
     * console.log(maybe.join()); // Just(5)
     *
     * const nothing = Nothing;
     * console.log(nothing.join()); // Nothing
     */
    join() {
        return this.value;
    }
    /**
     * Reduces the encapsulated value with an accumulator function.
     *
     * @param reducer The reducer function.
     * @param initialValue The initial accumulator value.
     * @returns The reduced value.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.reduce((acc, x) => acc + x, 10)); // 15
     *
     * const nothing = Nothing;
     * console.log(nothing.reduce((acc, x) => acc + x, 10)); // 10
     */
    reduce(reducer, initialValue) {
        return reducer(initialValue, this.value);
    }
    /**
     * Extends this `Just` by applying a function to it.
     *
     * @param f The function to apply.
     * @returns A new `Just` containing the result of applying `f`.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.extend(m => m.value * 2)); // Just(10)
     *
     * const nothing = Nothing;
     * console.log(nothing.extend(m => 0)); // Nothing
     */
    extend(f) {
        return new Just(f(this));
    }
    /**
     * Unsafely extracts the value from this `Just`.
     *
     * @returns The encapsulated value.
     * @throws Never throws for `Just`.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.unsafeCoerce()); // 5
     *
     * const nothing = Nothing;
     * // Throws: Error: Maybe#unsafeCoerce was ran on a Nothing
     * console.log(nothing.unsafeCoerce());
     */
    unsafeCoerce() {
        return this.value;
    }
    /**
     * Throws if this is `Nothing`.
     *
     * @returns The encapsulated value.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.throwOnEmpty()); // 5
     *
     * const nothing = Nothing;
     * // Throws: Error: Maybe is empty
     * console.log(nothing.throwOnEmpty());
     */
    throwOnEmpty() {
        return this.value;
    }
    /**
     * Returns the encapsulated value.
     *
     * @returns The encapsulated value.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.orDefault(10)); // 5
     *
     * const nothing = Nothing;
     * console.log(nothing.orDefault(10)); // 10
     */
    orDefault() {
        return this.value;
    }
    /**
     * Lazily returns the encapsulated value.
     *
     * @returns The encapsulated value.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.orDefaultLazy(() => 10)); // 5
     *
     * const nothing = Nothing;
     * console.log(nothing.orDefaultLazy(() => 10)); // 10
     */
    orDefaultLazy() {
        return this.value;
    }
    /**
     * Converts the encapsulated value to a list.
     *
     * @returns A single-element array containing the encapsulated value.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.toList()); // [5]
     *
     * const nothing = Nothing;
     * console.log(nothing.toList()); // []
     */
    toList() {
        return [this.value];
    }
    /**
     * Maps the encapsulated value or returns a default.
     *
     * @param f The function to apply to the value.
     * @returns The mapped value or the default value.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.mapOrDefault(x => x * 2, 10)); // 10
     *
     * const nothing = Nothing;
     * console.log(nothing.mapOrDefault(x => x * 2, 10)); // 10
     */
    mapOrDefault(f) {
        return f(this.value);
    }
    /**
     * Executes an effect for the encapsulated value.
     *
     * @param effect The function to execute.
     * @returns This `Just`.
     * @example
     * const maybe = new Just(5);
     * maybe.ifJust(console.log); // Logs: 5
     */
    ifJust(effect) {
        effect(this.value);
        return this;
    }
    /**
     * Executes an effect if this is `Nothing`.
     *
     * @returns This `Just`.
     * @example
     * const maybe = new Just(5);
     * maybe.ifNothing(() => console.log('Nothing here!')); // Nothing happens.
     *
     * const nothing = Nothing;
     * nothing.ifNothing(() => console.log('Nothing here!')); // Logs: "Nothing here!"
     */
    ifNothing() {
        return this;
    }
    /**
     * Filters the encapsulated value.
     *
     * @param pred A predicate function to filter the value.
     * @returns This `Just` if the predicate passes, otherwise `Nothing`.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.filter(x => x > 3)); // Just(5)
     * console.log(maybe.filter(x => x < 3)); // Nothing
     */
    filter(pred) {
        if (pred(this.value)) {
            return this;
        }
        return Nothing;
    }
    /**
     * Converts this `Just` to an `Either` with its value as `Right`.
     *
     * @returns A `Right` containing the encapsulated value.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.toEither()); // Right(5)
     *
     * const nothing = Nothing;
     * console.log(nothing.toEither('Error')); // Left('Error')
     */
    toEither() {
        return Right(this.value);
    }
    /**
     * Extracts the value from a `Just`.
     *
     * If you already know this is a `Just` it is preferred to just access `.value` as that will
     * ensure that the value can't be a `Nothing` as that property is not available on nothings.
     *
     * @returns The encapsulated value of the `Just`, or `undefined`.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.extract()); // 5
     *
     * const nothing = Nothing;
     * console.log(nothing.extract())  // undefined;
     */
    extract() {
        return this.value;
    }
    /**
     * @returns The same as `extract` but returns `null` for `Nothing`.
     */
    extractNullable() {
        return this.value;
    }
    /**
     * Converts this `Just` to a string representation.
     *
     * @returns A string in the format `Just(value)`.
     * @example
     * const maybe = new Just(5);
     * console.log(maybe.toString()); // "Just(5)"
     *
     * const nothing = Nothing;
     * console.log(nothing.toString()); // "Nothing"
     */
    toString() {
        return `Just(${String(this.value)})`;
    }
    /** @returns The value of the item, as an unknown. */
    toJSON() {
        return this.value;
    }
}
/**
 * Creates a `Maybe` from a nullable value.
 *
 * @param value A potentially null or undefined value.
 * @returns A `Just` if the value is not null/undefined, otherwise `Nothing`.
 */
function fromNullable(value) {
    if (value == null) {
        return Nothing;
    }
    return new Just(value);
}
/**
 * Creates a `Maybe` from a predicate function and a value.
 *
 * @param pred The predicate function to evaluate.
 * @param value The value to test against the predicate.
 * @returns A `Just` if the predicate returns `true`, otherwise `Nothing`.
 */
function fromPredicate(pred, value) {
    if (arguments.length >= 2) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return pred(value) ? new Just(value) : Nothing;
    }
    return (newValue) => fromPredicate(pred, newValue);
}
/**
 * Utility functions for working with `Maybe` values.
 */
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Maybe = Object.freeze({
    fromNullable,
    fromPredicate,
    /**
     * Creates a `Maybe` containing the provided value.
     *
     * @param value The value to wrap in a `Just`.
     * @returns A `Just` containing the value.
     */
    of(value) {
        return new Just(value);
    },
    /**
     * Converts a falsy value to a `Maybe`.
     *
     * @param value The value to evaluate.
     * @returns A `Just` if the value is truthy, otherwise `Nothing`.
     */
    fromFalsy(value) {
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (value) {
            return new Just(value);
        }
        return Nothing;
    },
    /**
     * Returns the `Nothing` instance, used as a zero value.
     *
     * @returns The `Nothing` instance.
     */
    zero() {
        return Nothing;
    },
    /**
     * Returns the `Nothing` instance, used as an empty value.
     *
     * @returns The `Nothing` instance.
     */
    empty() {
        return Nothing;
    },
    /**
     * Filters and extracts `Just` values from a list of `Maybe` instances.
     *
     * @param list The list of `Maybe` instances.
     * @returns An array of the unwrapped values of the `Just` instances.
     */
    catMaybes(list) {
        const result = [];
        for (const item of list) {
            if (item.isJust()) {
                result.push(item.value);
            }
        }
        return result;
    },
    /**
     * Applies a function to each element in a list and returns the resulting values
     * if the function returns a `Just`, otherwise skips the value.
     *
     * @param f The function to apply.
     * @param list The list of values.
     * @returns An array of the unwrapped values of the resulting `Just` instances.
     */
    mapMaybe(f, list) {
        const res = [];
        for (const item of list) {
            const mapped = f(item);
            if (mapped.isJust()) {
                res.push(mapped.value);
            }
        }
        return res;
    },
    /**
     * Encases a computation in a `Maybe`, catching exceptions and returning `Nothing` on failure.
     *
     * @param thunk The computation to execute.
     * @returns A `Just` with the result of the computation, or `Nothing` if an exception occurs.
     */
    encase(thunk) {
        try {
            return new Just(thunk());
        }
        catch {
            return Nothing;
        }
    },
    /**
     * Determines if a value is a `Maybe`.
     *
     * @param x The value to check.
     * @returns `true` if the value is a `Maybe`, otherwise `false`.
     */
    isMaybe(x) {
        return x instanceof Just || x === Nothing;
    },
    /**
     * Transforms a list of `Maybe` instances into a `Maybe` of a list, failing fast on `Nothing`.
     *
     * @param maybes The list of `Maybe` instances.
     * @returns A `Just` containing a list of the unwrapped values if all are `Just`,
     * otherwise `Nothing`.
     */
    sequence(maybes) {
        const result = [];
        for (const item of maybes) {
            if (item.isNothing()) {
                return item;
            }
            result.push(item.value);
        }
        return new Just(result);
    },
    /**
     * Converts an `Either` to a `Maybe`.
     *
     * @param val The `Either` instance.
     * @returns A `Just` if the `Either` is a `Right`, otherwise `Nothing`.
     */
    fromEither(val) {
        if (val.isRight()) {
            return new Just(val.extract());
        }
        return Nothing;
    },
});
